import { ReactElement, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heading } from '../../../components/Heading';
import { Paragraph } from '../../../components/Paragraph';
import { ChevronLeftIcon } from '@navikt/aksel-icons';
import classes from './UserDetailPage.module.css';
import { Routes } from '../../../enums/Routes';
import { useGetUserDetailQuery } from '../../../hooks/queries/useGetUserDetailQuery';
import { GhostLoader } from '../../../components/GhostLoader/GhostLoader';
import { Alert } from '../../../components/Alert/Alert';
import { ContainerCard } from '../../../components/ContainerCard/ContainerCard';
import { formatNorwegianDate } from '../../../utils/dateFormatters';
import type { UserDetail, UserRole, UserTeamMembership } from '../../../api/queries';

const ERROR_MESSAGE = 'Kunne ikke laste brukerdata. Prøv igjen senere.';
const NOT_FOUND_MESSAGE = 'Bruker ikke funnet.';

type StatCardData = {
  title: string;
  value: string | number;
  meta?: string;
};

const StatCard = ({ title, value, meta }: StatCardData): ReactElement => (
  <ContainerCard>
    <ContainerCard.Title>{title}</ContainerCard.Title>
    <ContainerCard.Body>
      <div className={classes.statRow}>
        <Heading as="h2" className={classes.statValue}>
          {value}
        </Heading>
        {meta && <Paragraph size="sm" className={classes.statMeta}>{meta}</Paragraph>}
      </div>
    </ContainerCard.Body>
  </ContainerCard>
);

const createStatsCards = (user: UserDetail): StatCardData[] => [
  {
    title: 'Roller',
    value: user.stats.totalRoles,
    meta: user.roles.length > 0 ? `Fordelt på ${new Set(user.roles.map(r => r.tenantId)).size} tenants` : undefined,
  },
  {
    title: 'Team-medlemskap',
    value: user.stats.totalTeamMemberships,
    meta: user.stats.leaderOfTeams > 0 ? `Leder for ${user.stats.leaderOfTeams} team` : undefined,
  },
  {
    title: 'Status',
    value: user.isPending ? 'Venter' : user.deletedAt ? 'Slettet' : 'Aktiv',
    meta: user.deletedAt ? `Slettet ${formatNorwegianDate(user.deletedAt)}` : undefined,
  },
];

export const UserDetailPage = (): ReactElement => {
  const { userId } = useParams<{ userId: string }>();
  const { data: user, isLoading, error } = useGetUserDetailQuery(Number(userId));

  const statsCards = useMemo(
    () => user ? createStatsCards(user) : [],
    [user]
  );

  if (isLoading) {
    return <GhostLoader />;
  }

  if (error) {
    return (
      <div className={classes.container}>
        <Alert variant="danger" size="md">
          <Paragraph>{ERROR_MESSAGE}</Paragraph>
        </Alert>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={classes.container}>
        <Alert variant="warning" size="md">
          <Paragraph>{NOT_FOUND_MESSAGE}</Paragraph>
        </Alert>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Link to={Routes.SystemAdminDashboard} className={classes.backLink}>
        <ChevronLeftIcon aria-hidden="true" fontSize="20px" />
        <span>Tilbake til dashboard</span>
      </Link>

      <div className={classes.header}>
        <Heading as="h1" className={classes.title}>{user.email}</Heading>
        <Paragraph size="md" className={classes.subtitle}>
          Opprettet {formatNorwegianDate(user.createdAt)}
        </Paragraph>
        <Paragraph size="md" className={classes.subtitle}>
          Tenant: <strong>{user.tenant.name}</strong>
          {user.tenant.partnerName && ` (Partner: ${user.tenant.partnerName})`}
        </Paragraph>
      </div>

      <div className={classes.statsGrid}>
        {statsCards.map((card) => (
          <StatCard key={card.title} {...card} />
        ))}
      </div>

      <div className={classes.section}>
        <Heading as="h2" className={classes.sectionTitle}>Roller</Heading>
        <ContainerCard>
          <ContainerCard.Body>
            {user.roles.length === 0 ? (
              <Paragraph>Ingen roller funnet.</Paragraph>
            ) : (
              <div className={classes.tableWrapper}>
                <table className={classes.dataTable}>
                  <thead>
                    <tr>
                      <th>Rolle</th>
                      <th>Tenant</th>
                      <th>Partner</th>
                      <th>Opprettet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {user.roles.map((role: UserRole, idx: number) => (
                      <tr key={idx}>
                        <td><strong>{role.role}</strong></td>
                        <td>{role.tenantName}</td>
                        <td>{role.partnerName || '-'}</td>
                        <td>{formatNorwegianDate(role.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ContainerCard.Body>
        </ContainerCard>
      </div>

      <div className={classes.section}>
        <Heading as="h2" className={classes.sectionTitle}>Team-medlemskap</Heading>
        <ContainerCard>
          <ContainerCard.Body>
            {user.teamMemberships.length === 0 ? (
              <Paragraph>Ingen team-medlemskap funnet.</Paragraph>
            ) : (
              <div className={classes.tableWrapper}>
                <table className={classes.dataTable}>
                  <thead>
                    <tr>
                      <th>Team</th>
                      <th>Organisasjon</th>
                      <th>Rolle</th>
                      <th>Leder</th>
                    </tr>
                  </thead>
                  <tbody>
                    {user.teamMemberships.map((membership: UserTeamMembership) => (
                      <tr key={membership.teamId}>
                        <td><strong>{membership.teamName}</strong></td>
                        <td>{membership.organizationName}</td>
                        <td>{membership.role}</td>
                        <td>{membership.isLeader ? 'Ja' : 'Nei'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ContainerCard.Body>
        </ContainerCard>
      </div>
    </div>
  );
};
